import { useNotificationsCount } from "@circle-react-shared/Notifications/useHeaderNotificationsApi";
import { roundedCount } from "@circle-react/helpers/notificationHelpers";
import { useChatNotification } from "@circle-react/hooks/chatsV2/useChatNotification";
import { useShouldShowMessagingRoutes } from "@circle-react/hooks/useIsMessagingEnabled";
import { IconButton } from "@circleco/compass/components/IconButton";
import { noop } from "lodash";

import { t } from "@/i18n-js/instance";
import { useCommunityRootPath } from "@/react/components/Mobile/hooks/useCommunityRootPath";
import { useIdleRenderPreload } from "@/react/components/Mobile/hooks/useIdleRenderPreload";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";

import { RouterLink } from "../components/RouterLink";

interface MobileBottomNavProps {
  currentPath: string;
}

/**
 * Format an unread count for the IconButton `counter` prop.
 * Returns `undefined` when there is nothing to show so the badge is hidden.
 */
const formatCounter = (count: number): string | undefined =>
  count > 0 ? String(roundedCount(count)) : undefined;

export const MobileBottomNav = ({ currentPath }: MobileBottomNavProps) => {
  const { currentCommunity } = usePunditUserContext();
  const { data: notificationCounts } = useNotificationsCount(
    currentCommunity?.id,
    noop,
  );
  const { unreadChatCount } = useChatNotification();
  const { shouldShowMessagingRoutes } = useShouldShowMessagingRoutes();
  const communityRootPath = useCommunityRootPath();
  // `undefined` falls back to the router's default intent preload.
  const preload = useIdleRenderPreload() ? "render" : undefined;

  const isHomeActive = currentPath.startsWith(communityRootPath);
  const isNotificationsActive = currentPath.startsWith("/notifications");
  const isMessagesActive = currentPath.startsWith("/messages");
  const isSearchActive = currentPath.startsWith("/search");

  return (
    <nav className="flex w-full justify-around">
      <IconButton
        renderLink={
          <RouterLink
            href={communityRootPath}
            preload={communityRootPath === "/feed" ? preload : undefined}
          />
        }
        icon={isHomeActive ? "home-filled" : "home"}
        variant={isHomeActive ? "secondary" : "ghost"}
        size="lg"
        aria-label={t("mobile_shell.home")}
      />
      <IconButton
        renderLink={<RouterLink href="/notifications" preload={preload} />}
        icon={isNotificationsActive ? "bell-filled" : "bell"}
        variant={isNotificationsActive ? "secondary" : "ghost"}
        size="lg"
        aria-label={t("mobile_shell.notifications")}
        counter={formatCounter(notificationCounts?.new_inbox_count ?? 0)}
      />
      {shouldShowMessagingRoutes && (
        <IconButton
          renderLink={<RouterLink href="/messages" preload={preload} />}
          icon={isMessagesActive ? "message-filled" : "message"}
          variant={isMessagesActive ? "secondary" : "ghost"}
          size="lg"
          aria-label={t("mobile_shell.messages")}
          counter={formatCounter(unreadChatCount)}
        />
      )}
      <IconButton
        renderLink={<RouterLink href="/search" />}
        icon="magnifying-glass"
        variant={isSearchActive ? "secondary" : "ghost"}
        size="lg"
        aria-label={t("mobile_shell.search")}
      />
    </nav>
  );
};
